import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-[#FFFFFF] border border-[#E4E4E7] px-4 py-3.5 text-sm text-[#09090B] placeholder:text-[#A1A1AA] focus:outline-none focus:border-[#6D28D9] focus:ring-1 focus:ring-[#6D28D9]/40 transition-colors duration-300";

export const AuthModal = () => {
  const { authModal, closeAuth, openAuth, login, register } = useAuth();
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({ name: "", email: "", password: "", consent: false });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (authModal) {
      setMode(authModal);
      setError("");
    }
  }, [authModal]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        const u = await login(form.email, form.password);
        toast.success(`Welcome back, ${u.name}`);
      } else {
        const u = await register({
          name: form.name,
          email: form.email,
          password: form.password,
          research_confirmed: form.consent,
        });
        toast.success(`Account created — welcome, ${u.name}`);
      }
      setForm({ name: "", email: "", password: "", consent: false });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {authModal && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeAuth}
            className="fixed inset-0 z-[80] bg-black/75 backdrop-blur-sm"
          />
          <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 pointer-events-none">
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            data-testid="auth-modal"
            className="pointer-events-auto w-full max-w-md max-h-[90vh] overflow-y-auto bg-[#FFFFFF] border border-[#E4E4E7] p-8"
          >
            <button onClick={closeAuth} data-testid="auth-modal-close" className="absolute top-4 right-4 p-2 text-[#71717A] hover:text-[#09090B] transition-colors">
              <X className="w-5 h-5" />
            </button>

            <p className="font-mono-clinical text-[10px] tracking-[0.3em] uppercase text-[#6D28D9] mb-3">Members Access</p>
            <h3 className="font-display text-2xl font-bold uppercase tracking-tight mb-6">
              {mode === "login" ? "Sign In" : "Create Account"}
            </h3>

            <div className="grid grid-cols-2 gap-px bg-[#E4E4E7] border border-[#E4E4E7] mb-6">
              <button
                onClick={() => { setMode("login"); setError(""); }}
                data-testid="auth-tab-login"
                className={`py-3 font-mono-clinical text-[11px] tracking-[0.2em] uppercase transition-colors duration-300 ${mode === "login" ? "bg-[#6D28D9] text-white" : "bg-[#FFFFFF] text-[#71717A] hover:text-[#09090B]"}`}
              >
                Sign In
              </button>
              <button
                onClick={() => { setMode("register"); setError(""); }}
                data-testid="auth-tab-register"
                className={`py-3 font-mono-clinical text-[11px] tracking-[0.2em] uppercase transition-colors duration-300 ${mode === "register" ? "bg-[#6D28D9] text-white" : "bg-[#FFFFFF] text-[#71717A] hover:text-[#09090B]"}`}
              >
                Register
              </button>
            </div>

            <form onSubmit={submit} data-testid={mode === "login" ? "auth-login-form" : "auth-register-form"} className="space-y-4">
              {mode === "register" && (
                <input required placeholder="Full name" data-testid="auth-register-name" value={form.name} onChange={set("name")} className={inputCls} />
              )}
              <input
                required
                type="email"
                placeholder="Email"
                data-testid={mode === "login" ? "auth-login-email" : "auth-register-email"}
                value={form.email}
                onChange={set("email")}
                className={inputCls}
              />
              <input
                required
                type="password"
                minLength={8}
                placeholder={mode === "register" ? "Password (min 8 characters)" : "Password"}
                data-testid={mode === "login" ? "auth-login-password" : "auth-register-password"}
                value={form.password}
                onChange={set("password")}
                className={inputCls}
              />

              {mode === "register" && (
                <label className="flex items-start gap-3 cursor-pointer group" data-testid="auth-register-consent-label">
                  <input
                    type="checkbox"
                    required
                    data-testid="auth-register-consent"
                    checked={form.consent}
                    onChange={set("consent")}
                    className="mt-0.5 w-4 h-4 shrink-0 accent-[#6D28D9] cursor-pointer"
                  />
                  <span className="text-[11px] text-[#71717A] leading-relaxed group-hover:text-[#71717A] transition-colors">
                    I confirm I am 21 years or older and these compounds are for laboratory research use only — not for human or veterinary use.
                  </span>
                </label>
              )}

              {error && (
                <p data-testid="auth-error" className="text-sm text-red-400 font-mono-clinical">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading || (mode === "register" && !form.consent)}
                data-testid={mode === "login" ? "auth-login-submit" : "auth-register-submit"}
                className="w-full py-4 bg-[#6D28D9] text-white font-mono-clinical text-xs tracking-[0.15em] uppercase font-bold hover:bg-[#5B21B6] transition-colors duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {mode === "login" ? "Sign In" : "Create Account & Enter"}
              </button>
            </form>

            <p className="flex items-center gap-2 mt-6 text-[10px] font-mono-clinical tracking-[0.1em] uppercase text-[#A1A1AA]">
              <ShieldCheck className="w-3.5 h-3.5 text-[#6D28D9]/60" strokeWidth={1.5} />
              Research use only — private members catalog
            </p>
            {mode === "register" && (
              <p data-testid="register-discount-note" className="mt-3 text-[11px] font-bold text-[#6D28D9]">
                Get $15 off your first order when you create an account.
              </p>
            )}
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};
